/**
 * 「从商品库选择」—— 全站引用商品资产的**唯一入口**。
 *
 * 用户口径（2026-10-08）：「这个可以让商品详情页可以直接引用，就不需要自己输出
 * 产品图片跟文案那些」；2026-10-09 追加：「**其他页面也可以引用这个商品库**啊，
 * 而且做好筛选跟分页」。
 *
 * 所以这个选择器的**唯一职责**是把一条商品档案交回给调用方（标题 / 卖点 / 图组），
 * 由调用方决定怎么铺进自己的表单。它不写任何东西、不碰草稿、不做「引用」这件事
 * 本身 —— 详情页 / 社媒图 / 广告图 / 文案 / 服饰图 / 视频各有各的字段名，
 * 选择器不该知道它们。
 *
 * ### 四条
 *
 * 1. **单选**。一次出一个商品（产品名 + 卖点 + 一组图是同一份东西的三面），
 *    多选会让「标题用哪一个」变成一道没人能回答的问题。
 * 2. **点卡片即选中**（不再要点一次「确定」）—— 这一步没有第二个决策。
 * 3. **取数在打开时才发生**，不在挂载时。生成页里最重的几页都挂着它，
 *    为一个可能不开的弹层常驻一条请求是没必要的。
 * 4. **筛选与分页都走服务端**（见下面那段）。库是会长大的，`perPage: 500`
 *    一次拉满在几百条时还看不出问题，上千条时就是「打开弹窗要等两秒」，
 *    而用户会以为是自己网络慢。
 *
 * ### 两种用法
 *
 *   · `<ProductAssetPicker open onClose onPick />` —— 弹层受调用方控制
 *     （详情页就是这么用的：它那颗按钮还兼着「AI 补卖点」的布局）。
 *   · `<ProductAssetPickerButton onPick />` —— 按钮 + 弹层一体，
 *     生成页的标准接入方式（一行接入，不重复一份 open state）。
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { UNCATEGORIZED_CATEGORY } from '@/api/types'
import type { ProductAsset, ProductAssetFacets } from '@/api/types'
import { Pager } from '@/components/PagedList'
import { SmartImage } from '@/components/SmartImage'
import { Icon } from '@/components/icons'
import { Button, Drawer, Empty, Input } from '@/components/ui'
import { PRODUCT_PICKER_PAGE_SIZE } from '@/config/productAssets'
import { productLanguageLabel } from '@/config/productAssets'
import { rememberFallback } from '@/lib/assetFallback'

export function ProductAssetPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean
  onClose: () => void
  /** 选中一条。**由调用方决定怎么用**（详情页要的是标题 + 卖点 + 图） */
  onPick: (asset: ProductAsset) => void
}) {
  const [items, setItems] = useState<ProductAsset[]>([])
  const [facets, setFacets] = useState<ProductAssetFacets | null>(null)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [language, setLanguage] = useState('')
  const [platform, setPlatform] = useState('')
  /**
   * 分类筛选 —— 与商品库页同一套三态（`''` 全部 / 哨兵 未分类 / 名字）。
   *
   * ⚠️ 弹层**只筛选，不管理**分类：改名、删除动的是「名下所有商品」，
   * 那件事必须发生在能看到全部商品与全部分类的地方（商品库页那个抽屉）。
   * 在一个「选一条商品」的弹层里改分类名，用户看不到自己影响了什么。
   */
  const [category, setCategory] = useState('')

  /*
   * 分页状态。**服务端分页**：`page` / `perPage` 原样发给后端，`total` 由后端下发。
   *
   * 为什么不复用 `usePagination`（客户端分页）：那个状态机假定「全集已经在手上」，
   * 而这里的全集可能上千条 —— 客户端分页意味着每次打开弹窗都先拉一遍全部，
   * 筛选也就只能在本页里筛（用户说的「做好筛选」正是这个反面）。
   * 但**渲染**仍走共用的 `Pager`：分页控件的实现只有那一份。
   */
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(PRODUCT_PICKER_PAGE_SIZE)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.listProductAssets({
        q: query || undefined,
        language: language || undefined,
        platform: platform || undefined,
        category: category || undefined,
        page,
        perPage,
      })
      res.items.forEach((a) => rememberFallback(a.coverUrl, a.coverLocal))
      /*
       * 越界回第 1 页：筛选条件把结果缩到 1 页，而用户停在第 3 页 ——
       * 后端如实返回空数组，界面就是**空白**，用户会认为「筛选坏了」。
       * 判据只能取「回来了 0 条且不是第 1 页」，不要拿 total 去算（total 可能
       * 已经变小，但这一屏是在旧 total 下请求的）。
       */
      if (!res.items.length && page > 1) {
        setPage(1)
        return
      }
      setItems(res.items)
      setTotal(res.total)
      setFailed(null)
    } catch (e) {
      setFailed(e instanceof Error ? e.message : '商品库加载失败')
    } finally {
      setLoading(false)
    }
  }, [query, language, platform, category, page, perPage])

  // 打开时才取数（见文件头第 3 条）；关闭不清空 —— 再打开时先看到上次那份，
  // 比先看到一屏骨架再跳一次舒服，而 `load` 会立刻覆盖它。
  useEffect(() => {
    if (!open) return
    void load()
    void api
      .productAssetFacets()
      .then(setFacets)
      .catch(() => setFacets(null))
  }, [open, load])

  // 搜索防抖，与商品资产页同一档（300ms）
  useEffect(() => {
    const t = window.setTimeout(() => setQuery(rawQuery.trim()), 300)
    return () => window.clearTimeout(t)
  }, [rawQuery])

  /**
   * 任一筛选条件变化 → 回第 1 页。
   *
   * ⚠️ **不能省**：停在第 5 页时把语言切到只有 2 条的那一档，不回第 1 页
   * 就是上面那条「越界回退」要救的空白页 —— 而那一跳在界面上是「闪一下」，
   * 用户只会觉得卡。这里直接掐掉，`load` 里的兜底留给「别处删了数据」。
   */
  useEffect(() => {
    setPage(1)
  }, [query, language, platform, category, perPage])

  const langs = facets?.languages ?? []
  const plats = facets?.platforms ?? []
  const cats = facets?.categories ?? []
  const uncategorized = facets?.uncategorized ?? 0
  // 分类行渲染条件与商品库页一致：有分类，或有未分类的货（分类允许只有 1 个）
  const showCategoryRow = cats.length > 0 || uncategorized > 0
  const pageCount = Math.max(1, Math.ceil(total / perPage))
  const from = total ? (page - 1) * perPage + 1 : 0
  const to = Math.min(page * perPage, total)
  const filtered = !!(query || language || platform || category)

  const resetFilters = () => {
    setRawQuery('')
    setLanguage('')
    setPlatform('')
    setCategory('')
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="从商品库选择"
      icon="box"
      width={760}
      footer={
        <>
          <span className="grow text-xs muted">
            选中后会把商品标题、卖点与产品图交回上一页（各页按自己的字段接）
          </span>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
        </>
      }
    >
      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <Input
          value={rawQuery}
          onChange={(e) => setRawQuery(e.target.value)}
          placeholder="搜索标题 / SKU / 卖点"
          aria-label="搜索商品"
          data-picker-search
          style={{ width: 240 }}
        />
        <span className="grow" />
        <span className="text-xs muted" data-picker-count>
          {loading ? '加载中…' : `共 ${total} 个商品`}
        </span>
      </div>

      {/*
        筛选条 —— 语言与平台，**选项与计数都来自 facets 的真实分布**。
        一条都没有的维度整块不渲染，而不是铺一排点了必空的 chip
        （与商品资产页同一口径）。筛选本身在右侧 chips 里就地切换。
      */}
      {showCategoryRow ? (
        <div className="asset-filter" data-picker-category-row>
          <span className="asset-filter__label">分类</span>
          <div className="asset-filter__chips">
            <button
              type="button"
              className="chip"
              data-active={category === ''}
              onClick={() => setCategory('')}
            >
              全部 {facets?.total ?? 0}
            </button>
            {cats.map((c) => (
              <button
                key={c.id || c.value}
                type="button"
                className="chip"
                data-active={category === c.value}
                data-category={c.value}
                onClick={() => setCategory(category === c.value ? '' : c.value)}
              >
                {c.value} {c.count}
              </button>
            ))}
            {uncategorized > 0 ? (
              <button
                type="button"
                className="chip"
                data-active={category === UNCATEGORIZED_CATEGORY}
                data-category-uncategorized
                onClick={() =>
                  setCategory(category === UNCATEGORIZED_CATEGORY ? '' : UNCATEGORIZED_CATEGORY)
                }
              >
                未分类 {uncategorized}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {langs.length > 1 ? (
        <div className="asset-filter" data-picker-lang-row>
          <span className="asset-filter__label">语言</span>
          <div className="asset-filter__chips">
            <button
              type="button"
              className="chip"
              data-active={language === ''}
              onClick={() => setLanguage('')}
            >
              全部 {facets?.total ?? 0}
            </button>
            {langs.map((l) => (
              <button
                key={l.value}
                type="button"
                className="chip"
                data-active={language === l.value}
                data-lang={l.value}
                onClick={() => setLanguage(language === l.value ? '' : l.value)}
              >
                {productLanguageLabel(l.value)} {l.count}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {plats.length > 1 ? (
        <div className="asset-filter" data-picker-platform-row>
          <span className="asset-filter__label">平台</span>
          <div className="asset-filter__chips">
            {plats.map((p) => (
              <button
                key={p.value}
                type="button"
                className="chip"
                data-active={platform === p.value}
                data-platform={p.value}
                onClick={() => setPlatform(platform === p.value ? '' : p.value)}
              >
                {p.value} {p.count}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {filtered ? (
        <div className="row row--between" style={{ margin: '2px 0 8px' }}>
          <span className="text-xs muted">筛选中：{total} 个匹配</span>
          <Button variant="ghost" size="sm" icon="x" onClick={resetFilters}>
            重置筛选
          </Button>
        </div>
      ) : null}

      {failed ? (
        <Empty
          icon="alert"
          title="商品库加载失败"
          text={failed}
          action={
            <Button variant="outline" size="sm" icon="refresh" onClick={() => void load()}>
              重试
            </Button>
          }
        />
      ) : !items.length ? (
        <Empty
          icon={loading ? 'refresh' : 'box'}
          title={
            loading ? '正在加载…' : filtered ? '没有匹配的商品' : '商品库还是空的'
          }
          text={
            loading || filtered
              ? undefined
              : '先在「商品库」里录一个商品（或从表格批量导入），这里就能直接引用了。'
          }
          action={
            loading ? undefined : filtered ? (
              <Button variant="outline" size="sm" icon="x" onClick={resetFilters}>
                重置筛选
              </Button>
            ) : (
              <Link className="btn btn--primary btn--sm" to="/product-assets">
                去商品库新增
              </Link>
            )
          }
        />
      ) : (
        <>
          <div className="pagrid" data-picker-grid>
            {items.map((a) => {
              const shot = a.coverUrl || a.images[0]?.url || ''
              return (
                <article className="pacard" key={a.id} data-picker-card data-product-id={a.id}>
                  <button
                    type="button"
                    className="pacard__shot"
                    title={`用「${a.title}」`}
                    aria-label={`用「${a.title}」`}
                    onClick={() => onPick(a)}
                  >
                    {shot ? (
                      <SmartImage src={shot} alt={a.title} />
                    ) : (
                      <span className="pacard__broken">
                        <Icon name="alert" size={16} />
                      </span>
                    )}
                    {a.images.length ? (
                      <span className="pacard__count">{a.images.length} 张</span>
                    ) : null}
                  </button>
                  <div className="pacard__body">
                    <div className="pacard__name" title={a.title}>
                      {a.title}
                    </div>
                    <div className="pacard__tags">
                      {/* 分类在最前 —— 与商品库页的卡片同一顺序，
                          用户在两个界面上认同一张卡的方式才一致 */}
                      <span className="pacard__tag" data-product-category={a.category}>
                        {a.category || '未分类'}
                      </span>
                      <span className="pacard__tag">
                        {productLanguageLabel(a.language) || '未设语言'}
                      </span>
                      {a.sku ? <span className="pacard__tag">{a.sku}</span> : null}
                    </div>
                    <div className="pacard__points" title={a.sellingPoints}>
                      {a.sellingPoints.split('\n').filter(Boolean).join(' · ') || '还没有卖点'}
                    </div>
                  </div>
                  <div className="pacard__acts">
                    <Button size="sm" variant="primary" icon="check" onClick={() => onPick(a)}>
                      用这个商品
                    </Button>
                  </div>
                </article>
              )
            })}
          </div>

          {/* 分页控件 = 共用那一份（PagedList 里的 `Pager`），这里只做服务端取数的接线 */}
          <Pager
            page={page}
            pageCount={pageCount}
            total={total}
            from={from}
            to={to}
            pageSize={perPage}
            onPage={setPage}
            onPageSize={setPerPage}
            pageSizes={[12, 24, 48, 96]}
            unit="个"
          />
        </>
      )}
    </Drawer>
  )
}

/**
 * 「从商品库选择」按钮 + 弹层 —— **生成页的标准接入方式**。
 *
 * 用法只有一行：
 * ```tsx
 * <ProductAssetPickerButton onPick={applyProductAsset} />
 * ```
 *
 * 为什么不把这个 `open` state 留给每个页面自己写：六个页面各写一遍
 * `useState(false)` + `onClose` + `<ProductAssetPicker …>` 就是同一件事的
 * 六份实现 —— 而它们迟早会只有五处被改（本项目「交互控件只许一份实现」那条）。
 *
 * `disabled` 留给调用方（比如某一档的字段结构接不住商品图时）。
 */
export function ProductAssetPickerButton({
  onPick,
  label = '从商品库选择',
  disabled,
  title,
  'data-testid': testId = 'from-library',
}: {
  onPick: (asset: ProductAsset) => void
  label?: string
  disabled?: boolean
  title?: string
  'data-testid'?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        className="btn btn--outline btn--sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={title}
        data-from-library={testId}
      >
        <Icon name="box" size={13} />
        {label}
      </button>
      <ProductAssetPicker
        open={open}
        onClose={() => setOpen(false)}
        onPick={(asset) => {
          setOpen(false)
          onPick(asset)
        }}
      />
    </>
  )
}
